import { functionFrom, FunctionLibrary, getFunctionSignature } from '../FunctionDefinition';
import { TYPES } from '../Types';

const equals = {
  [TYPES.duration.name]: functionFrom(
    'equals',
    [
      { name: 'left', type: TYPES.duration },
      { name: 'right', type: TYPES.duration },
    ] as const,
    TYPES.boolean,
    (_ctx, a, b) => a.total({ unit: 'seconds' }) === b.total({ unit: 'seconds' }),
    'Returns true if the two durations are equal (second-level precision).',
  ),
  [TYPES.zonedDateTime.name]: functionFrom(
    'equals',
    [
      { name: 'left', type: TYPES.zonedDateTime },
      { name: 'right', type: TYPES.zonedDateTime },
    ] as const,
    TYPES.boolean,
    (_ctx, a, b) => a.equals(b),
    'Returns true if the two ZonedDateTime values represent the same instant.',
  ),
  [TYPES.plainDate.name]: functionFrom(
    'equals',
    [
      { name: 'left', type: TYPES.plainDate },
      { name: 'right', type: TYPES.plainDate },
    ] as const,
    TYPES.boolean,
    (_ctx, a, b) => a.equals(b),
    'Returns true if the two dates are equal.',
  ),
  [TYPES.string.name]: functionFrom(
    'equals',
    [
      { name: 'left', type: TYPES.string },
      { name: 'right', type: TYPES.string },
    ] as const,
    TYPES.boolean,
    (_ctx, a, b) => a === b,
    'Returns true if the two strings are equal.',
  ),
  [TYPES.boolean.name]: functionFrom(
    'equals',
    [
      { name: 'left', type: TYPES.boolean },
      { name: 'right', type: TYPES.boolean },
    ] as const,
    TYPES.boolean,
    (_ctx, a, b) => a === b,
    'Returns true if the two boolean values are equal.',
  ),
  [TYPES.number.name]: functionFrom(
    'equals',
    [
      { name: 'left', type: TYPES.number },
      { name: 'right', type: TYPES.number },
    ] as const,
    TYPES.boolean,
    (_ctx, a, b) => a === b,
    'Returns true if the two numbers are equal.',
  ),
  [TYPES.plainTime.name]: functionFrom(
    'equals',
    [
      { name: 'left', type: TYPES.plainTime },
      { name: 'right', type: TYPES.plainTime },
    ] as const,
    TYPES.boolean,
    (_ctx, a, b) => a.equals(b),
    'Returns true if the two times are equal.',
  ),
};

const gt = {
  [TYPES.duration.name]: functionFrom(
    'gt',
    [
      { name: 'left', type: TYPES.duration },
      { name: 'right', type: TYPES.duration },
    ] as const,
    TYPES.boolean,
    (_ctx, a, b) => a.total({ unit: 'seconds' }) > b.total({ unit: 'seconds' }),
    'Returns true if the left duration is greater than the right duration.',
  ),
  [TYPES.zonedDateTime.name]: functionFrom(
    'gt',
    [
      { name: 'left', type: TYPES.zonedDateTime },
      { name: 'right', type: TYPES.zonedDateTime },
    ] as const,
    TYPES.boolean,
    (_ctx, a, b) => a.since(b).total({ unit: 'seconds' }) > 0,
    'Returns true if the left ZonedDateTime is after the right ZonedDateTime.',
  ),
  [TYPES.plainDate.name]: functionFrom(
    'gt',
    [
      { name: 'left', type: TYPES.plainDate },
      { name: 'right', type: TYPES.plainDate },
    ] as const,
    TYPES.boolean,
    (_ctx, a, b) => a.since(b).total({ unit: 'days' }) > 0,
    'Returns true if the left date is after the right date.',
  ),
  [TYPES.string.name]: functionFrom(
    'gt',
    [
      { name: 'left', type: TYPES.string },
      { name: 'right', type: TYPES.string },
    ] as const,
    TYPES.boolean,
    (_ctx, a, b) => a.localeCompare(b) > 0,
    'Returns true if the left string is greater than the right string.',
  ),
  [TYPES.number.name]: functionFrom(
    'gt',
    [
      { name: 'left', type: TYPES.number },
      { name: 'right', type: TYPES.number },
    ] as const,
    TYPES.boolean,
    (_ctx, a, b) => a > b,
    'Returns true if the left number is greater than the right number.',
  ),
  [TYPES.plainTime.name]: functionFrom(
    'gt',
    [
      { name: 'left', type: TYPES.plainTime },
      { name: 'right', type: TYPES.plainTime },
    ] as const,
    TYPES.boolean,
    (_ctx, a, b) => a.since(b).total({ unit: 'seconds' }) > 0,
    'Returns true if the left time is after the right time.',
  ),
};

const gte = {
  [TYPES.duration.name]: functionFrom(
    'gte',
    [
      { name: 'left', type: TYPES.duration },
      { name: 'right', type: TYPES.duration },
    ] as const,
    TYPES.boolean,
    (_ctx, a, b) => a.total({ unit: 'seconds' }) >= b.total({ unit: 'seconds' }),
    'Returns true if the left duration is greater than or equal to the right duration.',
  ),
  [TYPES.zonedDateTime.name]: functionFrom(
    'gte',
    [
      { name: 'left', type: TYPES.zonedDateTime },
      { name: 'right', type: TYPES.zonedDateTime },
    ] as const,
    TYPES.boolean,
    (_ctx, a, b) => a.since(b).total({ unit: 'seconds' }) >= 0,
    'Returns true if the left ZonedDateTime is after or equal to the right ZonedDateTime.',
  ),
  [TYPES.plainDate.name]: functionFrom(
    'gte',
    [
      { name: 'left', type: TYPES.plainDate },
      { name: 'right', type: TYPES.plainDate },
    ] as const,
    TYPES.boolean,
    (_ctx, a, b) => a.since(b).total({ unit: 'days' }) >= 0,
    'Returns true if the left date is after or equal to the right date.',
  ),
  [TYPES.string.name]: functionFrom(
    'gte',
    [
      { name: 'left', type: TYPES.string },
      { name: 'right', type: TYPES.string },
    ] as const,
    TYPES.boolean,
    (_ctx, a, b) => a.localeCompare(b) >= 0,
    'Returns true if the left string is greater than or equal to the right string.',
  ),
  [TYPES.number.name]: functionFrom(
    'gte',
    [
      { name: 'left', type: TYPES.number },
      { name: 'right', type: TYPES.number },
    ] as const,
    TYPES.boolean,
    (_ctx, a, b) => a >= b,
    'Returns true if the left number is greater than or equal to the right number.',
  ),
  [TYPES.plainTime.name]: functionFrom(
    'gte',
    [
      { name: 'left', type: TYPES.plainTime },
      { name: 'right', type: TYPES.plainTime },
    ] as const,
    TYPES.boolean,
    (_ctx, a, b) => a.since(b).total({ unit: 'seconds' }) >= 0,
    'Returns true if the left time is after or equal to the right time.',
  ),
};

const lt = {
  [TYPES.duration.name]: functionFrom(
    'lt',
    [
      { name: 'left', type: TYPES.duration },
      { name: 'right', type: TYPES.duration },
    ] as const,
    TYPES.boolean,
    (_ctx, a, b) => a.total({ unit: 'seconds' }) < b.total({ unit: 'seconds' }),
    'Returns true if the left duration is less than the right duration.',
  ),
  [TYPES.zonedDateTime.name]: functionFrom(
    'lt',
    [
      { name: 'left', type: TYPES.zonedDateTime },
      { name: 'right', type: TYPES.zonedDateTime },
    ] as const,
    TYPES.boolean,
    (_ctx, a, b) => a.since(b).total({ unit: 'seconds' }) < 0,
    'Returns true if the left ZonedDateTime is before the right ZonedDateTime.',
  ),
  [TYPES.plainDate.name]: functionFrom(
    'lt',
    [
      { name: 'left', type: TYPES.plainDate },
      { name: 'right', type: TYPES.plainDate },
    ] as const,
    TYPES.boolean,
    (_ctx, a, b) => a.since(b).total({ unit: 'days' }) < 0,
    'Returns true if the left date is before the right date.',
  ),
  [TYPES.string.name]: functionFrom(
    'lt',
    [
      { name: 'left', type: TYPES.string },
      { name: 'right', type: TYPES.string },
    ] as const,
    TYPES.boolean,
    (_ctx, a, b) => a.localeCompare(b) < 0,
    'Returns true if the left string is less than the right string.',
  ),
  [TYPES.number.name]: functionFrom(
    'lt',
    [
      { name: 'left', type: TYPES.number },
      { name: 'right', type: TYPES.number },
    ] as const,
    TYPES.boolean,
    (_ctx, a, b) => a < b,
    'Returns true if the left number is less than the right number.',
  ),
  [TYPES.plainTime.name]: functionFrom(
    'lt',
    [
      { name: 'left', type: TYPES.plainTime },
      { name: 'right', type: TYPES.plainTime },
    ] as const,
    TYPES.boolean,
    (_ctx, a, b) => a.since(b).total({ unit: 'seconds' }) < 0,
    'Returns true if the left time is before the right time.',
  ),
};

const lte = {
  [TYPES.duration.name]: functionFrom(
    'lte',
    [
      { name: 'left', type: TYPES.duration },
      { name: 'right', type: TYPES.duration },
    ] as const,
    TYPES.boolean,
    (_ctx, a, b) => a.total({ unit: 'seconds' }) <= b.total({ unit: 'seconds' }),
    'Returns true if the left duration is less than or equal to the right duration.',
  ),
  [TYPES.zonedDateTime.name]: functionFrom(
    'lte',
    [
      { name: 'left', type: TYPES.zonedDateTime },
      { name: 'right', type: TYPES.zonedDateTime },
    ] as const,
    TYPES.boolean,
    (_ctx, a, b) => a.since(b).total({ unit: 'seconds' }) <= 0,
    'Returns true if the left ZonedDateTime is before or equal to the right ZonedDateTime.',
  ),
  [TYPES.plainDate.name]: functionFrom(
    'lte',
    [
      { name: 'left', type: TYPES.plainDate },
      { name: 'right', type: TYPES.plainDate },
    ] as const,
    TYPES.boolean,
    (_ctx, a, b) => a.since(b).total({ unit: 'days' }) <= 0,
    'Returns true if the left date is before or equal to the right date.',
  ),
  [TYPES.string.name]: functionFrom(
    'lte',
    [
      { name: 'left', type: TYPES.string },
      { name: 'right', type: TYPES.string },
    ] as const,
    TYPES.boolean,
    (_ctx, a, b) => a.localeCompare(b) <= 0,
    'Returns true if the left string is less than or equal to the right string.',
  ),
  [TYPES.number.name]: functionFrom(
    'lte',
    [
      { name: 'left', type: TYPES.number },
      { name: 'right', type: TYPES.number },
    ] as const,
    TYPES.boolean,
    (_ctx, a, b) => a <= b,
    'Returns true if the left number is less than or equal to the right number.',
  ),
  [TYPES.plainTime.name]: functionFrom(
    'lte',
    [
      { name: 'left', type: TYPES.plainTime },
      { name: 'right', type: TYPES.plainTime },
    ] as const,
    TYPES.boolean,
    (_ctx, a, b) => a.since(b).total({ unit: 'seconds' }) <= 0,
    'Returns true if the left time is before or equal to the right time.',
  ),
};

export const COMPARISON_FUNCTIONS = {
  [getFunctionSignature(equals.duration)]: equals.duration,
  [getFunctionSignature(equals.zonedDateTime)]: equals.zonedDateTime,
  [getFunctionSignature(equals.plainDate)]: equals.plainDate,
  [getFunctionSignature(equals.string)]: equals.string,
  [getFunctionSignature(equals.boolean)]: equals.boolean,
  [getFunctionSignature(equals.number)]: equals.number,
  [getFunctionSignature(equals.plainTime)]: equals.plainTime,
  [getFunctionSignature(gt.duration)]: gt.duration,
  [getFunctionSignature(gt.zonedDateTime)]: gt.zonedDateTime,
  [getFunctionSignature(gt.plainDate)]: gt.plainDate,
  [getFunctionSignature(gt.string)]: gt.string,
  [getFunctionSignature(gt.number)]: gt.number,
  [getFunctionSignature(gt.plainTime)]: gt.plainTime,
  [getFunctionSignature(gte.duration)]: gte.duration,
  [getFunctionSignature(gte.zonedDateTime)]: gte.zonedDateTime,
  [getFunctionSignature(gte.plainDate)]: gte.plainDate,
  [getFunctionSignature(gte.string)]: gte.string,
  [getFunctionSignature(gte.number)]: gte.number,
  [getFunctionSignature(gte.plainTime)]: gte.plainTime,
  [getFunctionSignature(lt.duration)]: lt.duration,
  [getFunctionSignature(lt.zonedDateTime)]: lt.zonedDateTime,
  [getFunctionSignature(lt.plainDate)]: lt.plainDate,
  [getFunctionSignature(lt.string)]: lt.string,
  [getFunctionSignature(lt.number)]: lt.number,
  [getFunctionSignature(lt.plainTime)]: lt.plainTime,
  [getFunctionSignature(lte.duration)]: lte.duration,
  [getFunctionSignature(lte.zonedDateTime)]: lte.zonedDateTime,
  [getFunctionSignature(lte.plainDate)]: lte.plainDate,
  [getFunctionSignature(lte.string)]: lte.string,
  [getFunctionSignature(lte.number)]: lte.number,
  [getFunctionSignature(lte.plainTime)]: lte.plainTime,
} satisfies FunctionLibrary;
